import React, { useState, useEffect, useCallback } from "react";
import {
    Toolbar,
    Typography,
    Box,
    Stack,
    Paper,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Collapse,
    FormControl,
    Select,
    TextField,
    MenuItem,
    InputAdornment,
    Divider,
    Avatar, Tooltip, Menu,
    Chip,
    Alert,
    useMediaQuery,
} from "@mui/material";
import { AccountCircleOutlined, ExpandLess, ExpandMore, FlightLand, PrintDisabledSharp, PrintRounded, PrintTwoTone, RefreshOutlined, RefreshRounded, RefreshSharp, SaveAlt, SaveAltOutlined, SaveAltSharp, SaveAltTwoTone, SaveAsRounded, SaveAsSharp, SaveAsTwoTone, SavedSearch, SaveOutlined, SaveRounded, SaveTwoTone } from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff"; // Departure Icon
import FlightLandIcon from "@mui/icons-material/FlightLand"; // Arrival Icon
import MenuIcon from "@mui/icons-material/Menu";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import '../styles/PayLoadEstimatesCSS.scss';
import { AutorenewTwoTone } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import { getReportData } from "../services/payLoadEstimatesapi/payLoadEstimatesAPI"
import { reportTemplates, ReportColumn } from "../template/payloadReportTemplate";
import { exportColumns } from "../template/exportReportTemplate";
import { useAuth } from "../context/AuthContext";
import { exportToCSV, exportToExcel, printReport } from "../utils/exportUtils";
import PrintDialog from "./PrintDialog"; // Import the modal component
import { getErrorMessage, createErrorTelemetryProperties, logError, logInformation, trackError } from '../utils/AppInsightUtils';
import LoadingSpinner from './LoadingSpinner';
import { appSettings } from '../appSettings';

const PayloadEstimates: React.FC = () => {
    const [pageSize, setPageSize] = useState<number>(50);
    const [page, setPage] = useState<number>(0);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [openMenu, setOpenMenu] = useState<{ [key: string]: boolean }>({
        Today: true,
        Tomorrow: false,
    });
    const [data, setData] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [selectedReport, setSelectedReport] = useState(reportTemplates.todayArrivals);

    const { isLoading, userData } = useAuth();
    const username = userData?.profile.name;
    const email = userData?.profile.email;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const [notesAnchorEl, setNotesAnchorEl] = useState<null | HTMLElement>(null);
    const handleNotesOpen = (event: React.MouseEvent<HTMLElement>) => setNotesAnchorEl(event.currentTarget);
    const handleNotesClose = () => setNotesAnchorEl(null);
    const [open, setOpen] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const isMobile = useMediaQuery("(max-width:900px)");
    const now = new Date();

    const formatted = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    // Helper function to enhance columns with conditional green styling for Protected Seats
    const enhanceColumnsWithConditionalStyling = (cols: any[]) => {
        return cols.map((col) => {
            if (col.field && col.field.includes('ProtectedSeats')) {
                return {
                    ...col,
                    renderCell: (params: any) => {
                        const value = params.value;
                        const isGreen = value > 0;
                        return (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: isGreen ? '#28a745' : 'inherit',
                                    fontWeight: isGreen ? 600 : 'inherit',
                                    fontSize: '1.05rem',
                                }}
                            >
                                {value}
                            </Box>
                        );
                    },
                };
            }
            return col;
        });
    };
  
    const fetchReportData = useCallback(async (query: string, page: number) => {
        if (!selectedReport) return;

        setLoading(true);
        setError(null);
        setData([]); // Clear old data before fetching

        try {
            logInformation('[PayLoadEstimates] [fetchReportData]: Starting to fetch Report data for :' + selectedReport.name, 'PayloadEstimates', 'fetchReportData');
            const responseData = await getReportData(selectedReport.endpoint, query, page);
            logInformation(`[PayLoadEstimates] [fetchReportData]: API result successfully retrieved for report ` + selectedReport.name + `: ${responseData}`, 'PayloadEstimates', 'fetchReportData');
            if (responseData) {
                setData(responseData);
            }
            else {
                logError(getErrorMessage(new Error(`API call succeeded but returned no data.`)),
                    'PayLoadEstimates',
                    'fetchReportData',
                    createErrorTelemetryProperties(new Error(`Failed to fetch report data for ` + selectedReport.name)));
                trackError(new Error(`API call succeeded but returned no data.`));
            }
        } catch (error) {
            const message = getErrorMessage(error as Error);
            setError(message);

            logError(message,
                'PayLoadEstimates',
                'fetchReportData',
                createErrorTelemetryProperties(error as Error));
            trackError(error as Error);
        }

        setLoading(false);
    }, [selectedReport]);


    // Fetch Data on report selection change
    useEffect(() => {
        setData([]); //  Clear old data
        setColumns([]); //  Clear old columns

        if (selectedReport) {
            fetchReportData("", 20);
        }
    }, [selectedReport]); //  Data refreshes when selectedReport changes


    // Toggle expandable menu
    const handleMenuClick = (menu: string) => {
        setOpenMenu((prev) => ({ ...prev, [menu]: !prev[menu] }));
    };

    // Update selected report
    const handleReportSelection = (reportKey: keyof typeof reportTemplates) => {
        setSelectedReport(reportTemplates[reportKey]);
        if (isMobile) {
            setMobileDrawerOpen(false);
        }
    };

    const isSelected = (reportKey: keyof typeof reportTemplates) => selectedReport === reportTemplates[reportKey];

    const handleRefresh = () => {

        setData([]); //  Clear old data
        setColumns([]); // Clear old columns

        if (selectedReport) {
            fetchReportData("", 20);
        }

    } //  Data refreshes when selectedReport changes

    const filteredData = data.map((row: any) => {
        const filteredRow: any = {};
        const columns = selectedReport.columns as GridColDef[];
        columns.forEach((col) => {
            if (col.field in row) {
                filteredRow[col.field] = row[col.field];
            }
        });
        return filteredRow;
    });
 
    const handleExport = (format: "csv" | "excel" | "Print") => {
        const exportedAt = new Date().toLocaleString();

        if (format === "excel")
            exportToExcel(filteredData, exportColumns[selectedReport.id], selectedReport.name, exportedAt);
        else if (format === "csv")
            exportToCSV(filteredData, exportColumns[selectedReport.id], selectedReport.name, exportedAt);

    };

    const isTomorrowReport = selectedReport?.id?.startsWith("tomorrow");

    const groupedHeaders = isTomorrowReport
      ? [
          { label: "Tomorrow", colSpan: 9 },
          { label: "", colSpan: 1 },
          { label: "Estimated", colSpan: 3 },
        ]
      : [
          { label: "Today", colSpan: 9 },
          { label: "Estimated", colSpan: 4 },
          { label: "Actual", colSpan: 3 },
        ];

    const totalGridWidth =
    selectedReport.columns.reduce((sum, col) => sum + (col.minwidth || 130), 0) + "px";
  
  const columnWidths = selectedReport.columns
    .map((col) => `${col.minwidth || 130}px`)
    .join(" ");


    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const reportNavigation = (
        <List>
            <ListItemButton onClick={() => handleMenuClick("Today")} sx={{ pl: 2 }} className="list-item">
                <ListItemText primary="Today" />
                {openMenu.Today ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={openMenu.Today} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => handleReportSelection("todayArrivals")}
                        className={`list-item nested-list ${isSelected("todayArrivals") ? "selected-report" : ""}`}>
                        <ListItemIcon><FlightLandIcon className="list-icon" /></ListItemIcon>
                        <ListItemText primary="Arrivals" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => handleReportSelection("todayDeparture")}
                        className={`list-item nested-list ${isSelected("todayDeparture") ? "selected-report" : ""}`}>
                        <ListItemIcon><FlightTakeoffIcon className="list-icon" /></ListItemIcon>
                        <ListItemText primary="Departure" />
                    </ListItemButton>
                    <Divider sx={{ borderRadius: "1px", height: "0.5px", backgroundColor: "#EEEEEE", marginY: 2 }} />
                </List>
            </Collapse>

            <ListItemButton onClick={() => handleMenuClick("Tomorrow")} sx={{ pl: 2 }} className="list-item">
                <ListItemText primary="Tomorrow" />
                {openMenu.Tomorrow ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={openMenu.Tomorrow} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => handleReportSelection("tomorrowArrival")}
                        className={`list-item nested-list ${isSelected("tomorrowArrival") ? "selected-report" : ""}`}>
                        <ListItemIcon><FlightLandIcon className="list-icon" /></ListItemIcon>
                        <ListItemText primary="Arrivals" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => handleReportSelection("tomorrowDeparture")}
                        className={`list-item nested-list ${isSelected("tomorrowDeparture") ? "selected-report" : ""}`}>
                        <ListItemIcon><FlightTakeoffIcon className="list-icon" /></ListItemIcon>
                        <ListItemText primary="Departure" />
                    </ListItemButton>
                </List>
            </Collapse>
        </List>
    );



    return (
        <Box sx={{ display: "flex", height: "100vh", overflowY: "scroll",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge
            "&::-webkit-scrollbar": {
              display: "none", // Chrome/Safari
            },  }}>

            {/* Left Panel */}
            <Stack
                sx={{
                    width: { xs: '100%', sm: 240, md: 280 },//280,//{ xs: 220, sm: 250, md: 300, lg: 320 },
                    minWidth: 220, // Set minWidth
                    maxWidth: 320, // Set maxWidth
                    flexDirection: "column",
                    p: 2,
                    minHeight: "100vh",// Set minHeight for testing
                    Height: "100%",
                    display: { xs: "none", md: "flex" },

                }}
            >
                {/* Logo (Fixed at the top) */}
                <Tooltip 
                    title={
                        <Box sx={{ textAlign: 'left' }}>
                            <div>Build: {appSettings.BUILD_VERSION}</div>
                            <div>Built: {appSettings.BUILD_DATE}</div>
                            <div>Env: {appSettings.env || 'local'}</div>
                        </Box>
                    } 
                    arrow
                >
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 0, pb: 0 }}>
                        <img src="/logo.png" alt="Logo" style={{ width: "80%", maxWidth: "140px", height: 50, maxHeight: 50 }} />
                    </Box>
                </Tooltip>

                {/* Divider between Logo and Logged-in User */}
                <Divider sx={{
                    borderRadius: "1px",
                    height: "0.5px",  // Make the divider line visible
                    backgroundColor: "#EEEEEE",  // Set the divider color
                    marginY: 1,  // Adjust spacing if needed
                }} />

                {/* Logged-in User and Drawer in a Stack */}
                <Stack sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    {/* Logged-in User Info */}
                    <Box sx={{ display: "flex", alignItems: "center", px: 0, py: 2, pb: 0, color: "#00274A" }}>

                        <Avatar sx={{ bgcolor: "transparent" }}>
                            <AccountCircleOutlined sx={{ fontSize: 32, mr: 1, color: "#73889C" }} />
                        </Avatar>

                        {/* User Details */}
                        <Stack>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.85rem" }}>
                                {username}
                            </Typography>
                            <Typography variant="caption" color="gray" sx={{ fontSize: "0.65rem", wordBreak: "break-word" }}>
                                {email}
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Sidebar Drawer  */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            flexGrow: 1,
                            "& .MuiDrawer-paper": { width: "100%", bgcolor: "transparent", color: "white", position: "relative", paddingTop: 0, border: "none" },
                        }}
                    >
                        <Toolbar /> {/* Ensures proper spacing */}
                        {reportNavigation}
                    </Drawer>
                </Stack>
            </Stack>

            <Drawer
                anchor="left"
                open={mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
                sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiDrawer-paper": {
                        width: 280,
                        boxSizing: "border-box",
                        p: 2,
                    },
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5, pb: 0.5 }}>
                    <img src="/logo.png" alt="Logo" style={{ width: "80%", maxWidth: "140px", height: 50, maxHeight: 50 }} />
                </Box>
                <Divider sx={{ borderRadius: "1px", height: "0.5px", backgroundColor: "#EEEEEE", marginY: 1 }} />
                <Box sx={{ display: "flex", alignItems: "center", px: 0, py: 1.5, color: "#00274A" }}>
                    <Avatar sx={{ bgcolor: "transparent" }}>
                        <AccountCircleOutlined sx={{ fontSize: 32, mr: 1, color: "#73889C" }} />
                    </Avatar>
                    <Stack>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.85rem" }}>
                            {username}
                        </Typography>
                        <Typography variant="caption" color="gray" sx={{ fontSize: "0.65rem", wordBreak: "break-word" }}>
                            {email}
                        </Typography>
                    </Stack>
                </Box>
                {reportNavigation}
            </Drawer>

            {/* Right Panel */}
            <Box sx={{
                flexGrow: 1, display: "flex", flexDirection: "column", p: 0
                , minHeight: "100vh",
                Height: "100%",
                overflowX: "hidden"// added to make grid scrollable
                
            }}>
                {/* Header with logo height match */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        mb: 0,
                        minHeight: 75,  // Ensures the header stays fixed in height
                        maxHeight: 75,  // Prevents resizing
                        overflow: "hidden", backgroundColor: "#003A67"
                    }} // Match the height of the logo container
                >
                    {/* Header */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.5 }}>
                        {isMobile ? (
                            <IconButton
                                size="small"
                                onClick={() => setMobileDrawerOpen(true)}
                                sx={{ color: "#fff" }}
                                aria-label="Open report navigation"
                            >
                                <MenuIcon />
                            </IconButton>
                        ) : null}
                        <Typography variant="h6" className="app-title" sx={{ mb: 0 }}>
                            {/* Task 1592484 Payload estimates 2.0 - Title change request */ }
                            Hawaii - Payload Estimates
                        </Typography>
                        <Chip
                            label={appSettings.env || "local"}
                            size="small"
                            sx={{
                                bgcolor: "rgba(255,255,255,0.12)",
                                color: "#fff",
                                border: "1px solid rgba(255,255,255,0.24)",
                                fontWeight: 700,
                            }}
                        />
                    </Box>


                </Stack>


                {/* Search, Dropdown & Buttons */}
                {/* Search, Dropdown & Buttons */}
                <Paper
                    elevation={0}
                    className="toolbar-card"
                    sx={{
                        mb: 2,
                        mx: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        border: "1px solid #E6EDF3",
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1.5}
                    >
                        <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#003A67" }}>
                                    {selectedReport.name}
                                </Typography>
                                {isTomorrowReport ? (
                                    <Chip
                                        icon={<InfoOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                                        label="Notes"
                                        size="small"
                                        clickable
                                        onClick={handleNotesOpen}
                                        sx={{
                                            border: "1px solid #9ec6ea",
                                            backgroundColor: "#edf5ff",
                                            color: "#0B4F8A",
                                            fontWeight: 700,
                                        }}
                                    />
                                ) : null}
                                <Menu
                                    anchorEl={notesAnchorEl}
                                    open={Boolean(notesAnchorEl)}
                                    onClose={handleNotesClose}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                    sx={{ "& .MuiPaper-root": { maxWidth: 440, p: 0.5 } }}
                                >
                                    <Box sx={{ px: 1.5, py: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75, color: "#003A67" }}>
                                            Tomorrow Reports Notes
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.75 }}>
                                            5:00am Dispatch estimates allowable payload for the day
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.75 }}>
                                            7:00pm Dispatch estimates allowable payload for the following day and CLP updates kid counts
                                        </Typography>
                                        <Typography variant="body2">
                                            Vols: Greater than 10 review for possible Tech Stop
                                        </Typography>
                                    </Box>
                                </Menu>
                            </Stack>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                                <Chip label={`${data.length} rows`} size="small" className="status-chip" />
                                <Chip label={`Updated ${formatted}`} size="small" className="status-chip" />
                            </Stack>
                        </Box>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                flexShrink: 0,
                                justifyContent: { xs: "flex-start", sm: "flex-end" },
                                width: { xs: "100%", sm: "auto" },
                            }}
                        >
                            <Tooltip title="Export Data">
                                <IconButton onClick={handleMenuOpen} size="small" className="action-icon-button">
                                    <SaveOutlined className="action-buttons" />
                                </IconButton>
                            </Tooltip>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                                <MenuItem onClick={() => handleExport("excel")}>Save as Excel</MenuItem>
                                <MenuItem onClick={() => handleExport("csv")}>Save as CSV</MenuItem>
                            </Menu>
                            <Tooltip title="Print Report">
                                <IconButton onClick={handleOpen} size="small" className="action-icon-button">
                                    <PrintOutlined className="action-buttons" />
                                </IconButton>
                            </Tooltip>

                            <PrintDialog
                                open={open}
                                onClose={handleClose}
                                data={filteredData}
                                exportColumns={exportColumns[selectedReport.id]}
                                reportTitle={selectedReport.name}
                                reportDate={new Date().toLocaleDateString()}
                            />
                        </Stack>
                    </Stack>
                </Paper>

                {/* Data Grid */}
                                <Paper
    elevation={0}
    sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 100px)",
        overflow: "hidden",
        mx: 1,
        mb: 1,
        borderRadius: 2,
        border: "1px solid #E6EDF3",
        background: "#fff",
    }}
>
  {/* OUTER scroll container */}
  <Box
    sx={{
      flexGrow: 1,
      overflow: "auto" ,
      
     
    }}
  >
    {/* INNER container with fixed width matching totalGridWidth */}
    <Box sx={{ width: totalGridWidth, minWidth: totalGridWidth }}>
      {/* STICKY GROUP HEADER */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1200,
          display: "grid",
          gridTemplateColumns: columnWidths,
          backgroundColor: "#f5f5f5",
          borderBottom: "2px solid #ccc",
          fontWeight: "bold",
        }}
      >
        {groupedHeaders.map((group, index) => {
          const isLast = index === groupedHeaders.length - 1;
          return (
            <Box
              key={index}
              sx={{
                gridColumn: `span ${group.colSpan}`,
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                position: "relative",
                //borderRight:"1px solid #ccc",
              }}
            >
              <span>{group.label}</span>
              {isLast && (
                <IconButton
                  onClick={handleRefresh}
                  size="small"
                  className="action-buttons"
                  sx={{
                    p: 0,
                    position: "absolute",
                    right: 15,
                  }}
                >
                  <AutorenewTwoTone fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>

            {/* DATAGRID */}
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <LoadingSpinner size="large" />
                    <Typography variant="body2" sx={{ color: '#73889C', fontWeight: 600 }}>
                        Loading {selectedReport.name.toLowerCase()}...
                    </Typography>
                </Box>
            ) : (
                <>
                    {error ? (
                        <Box sx={{ p: 2 }}>
                            <Alert severity="error" variant="outlined">
                                {error}
                            </Alert>
                        </Box>
                    ) : null}
                    {!error && data.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center', alignItems: 'center', minHeight: 360, color: '#73889C' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#003A67' }}>
                                No rows to display
                            </Typography>
                            <Typography variant="body2">
                                Try refreshing the report or selecting a different tab.
                            </Typography>
                        </Box>
                    ) : (
                        <DataGrid
                            className="custom-data-grid"
                            key={selectedReport.name}
                            rows={data.map((row, index) => ({ ...row, id: index + 1 }))}
                            columns={enhanceColumnsWithConditionalStyling(selectedReport.columns)}
                            paginationModel={{ page, pageSize }}
                            onPaginationModelChange={(model) => {
                                setPage(model.page);
                                setPageSize(model.pageSize);
                            }}
                            pageSizeOptions={[5, 10, 20]}
                            virtualizeColumnsWithAutoRowHeight
                            disableColumnResize
                            disableRowSelectionOnClick
                            rowHeight={40}
                            sx={{
                                minWidth: totalGridWidth,
                                border: "none",
                                "& .MuiDataGrid-columnHeaders": {
                                    position: "sticky",
                                    top: 40,
                                    backgroundColor: "#fff",
                                    zIndex: 1000,
                                    maxHeight: "none",
                                },
                                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                                    outline: "2px solid #0074CA",
                                    outlineOffset: "-2px",
                                },
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                                    outline: "2px solid #0074CA",
                                    outlineOffset: "-2px",
                                },
                                "& .MuiDataGrid-virtualScroller": {
                                    overflowX: "hidden",
                                    maxHeight: "calc(100vh - 180px)",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#edf5ff",
                                },
                            }}
                        />
                    )}
                </>
            )}
    </Box>
  </Box>
</Paper>





            </Box>

        </Box>

    );
};


export default PayloadEstimates;
